/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global interpolate, gettext */
import React from 'react'; // eslint-disable-line
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Map, List } from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import ReactRouterPropTypes from 'react-router-prop-types';
import { withRouter } from 'react-router';
import fetch from 'isomorphic-fetch';

import { MODELS, ACTIONS, can } from 'capabilities';
import { isPending } from 'consts/requestStates';
import { canEdit, isFailedCreation } from 'models/remoteCreatableObject';
import Loading from 'components/Loading';
import { LOAD_ITEM, UPDATE_ITEM, updateItem, TENANT, loadItem } from 'actions/items';
import { lockItem, unlockItem, hideTenantUser } from 'actions/asyncActions';
import { resetRequestState } from 'actions/actions';
import { openConfirmationModalThen, setUserFieldCount } from 'actions/uiActions';
import { apiRequest } from 'utils';
import EditTenantForm from './EditTenantForm';


const TENANT_CONFIG_FIELDS = [
  'salesforce_id',
  'name',
  'jhana_company_id',
  'administrator_level',
  'facilitator_level',
  'learner_level',
  'geo_ip_restrictions',
  'ts_and_cs',
  'display_company_and_employee_id',
  'client_logo',
  'jhana_enabled',
  'platform_first',
];

const TenantReportedClientAdminRow = (props) => {
  const { firstName, lastName, email } = props;
  return (
    <tr>
      <td>{firstName}</td>
      <td>{lastName}</td>
      <td>{email}</td>
    </tr>
  );
};

export class EditTenantPage extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      rows: [<TenantReportedClientAdminRow firstName="Loading..." lastName="" email="" />],
    };
    if (typeof window === 'undefined') {
      global.window = {};
      global.document = {};
    }
    this.window = global.window;
    this.document = global.document;
  }

  componentWillMount() {
    this.props.resetTenantRequestState();
    this.props.lockTenant()
      .catch(() => {}) // Load even if lock failed
      .then(() => this.props.loadTenant())
      .then(() => this.getTenantReportedClientAdminsRows());
  }

  getTenantReportedClientAdminsRows() {
    let rowData = [];
    const newRows = [];
    fetch(
      `/api/tenants/${this.props.tenant.get('id')}/client_admins/`, {
        method: 'get',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
      .then(response => response.json())
      .then((json) => {
        rowData = json;
        for (let i = 0; i < rowData.length; i += 1) {
          newRows.push(
            <TenantReportedClientAdminRow
              firstName={rowData[i].firstname}
              lastName={rowData[i].lastname}
              email={rowData[i].email}
            />,
          );
        }
        this.setState({ rows: newRows });
      })
      .catch(() => {
        this.setState({
          rows: [<TenantReportedClientAdminRow firstName="Error!" lastName="" email="" />],
        });
      });
  }

  navigateToList() {
    this.props.unlockTenant()
      .catch(() => {}) // Return to listing even if unlock failed
      .then(() => this.props.history.push('/tenants/'));
  }

  updateTenantAndNavigateToList(data) {
    // If client_logo is untouched it will still be a URL string (not a File object)
    // In this case, don't submit it
    if (data.client_logo && typeof data.client_logo === 'string') {
      delete data.client_logo; // eslint-disable-line no-param-reassign
    }

    this.props.updateTenant(data)
      .then(() => this.navigateToList())
      .catch(() => {
        const errors = this.document.getElementsByClassName('error');
        let didscroll = false;
        Object.keys(errors).forEach((err) => {
          if (!didscroll && errors[err].innerText !== '') {
            didscroll = true;
            const bodyRect = this.document.body.getBoundingClientRect();
            const elemRect = errors[err].getBoundingClientRect();
            const offset = elemRect.top - bodyRect.top;
            this.window.scrollTo(0, offset - 100);
          }
        });
      });
  }

  confirmAndHideUser(userId) {
    this.props.openConfirmationModalThen(
      gettext('Are you sure you wish to hide this user?'),
      () => this.props.hideTenantUser(this.props.tenant.get('id'), userId),
    );
  }

  syncToIP(tenantId) {
    return apiRequest(
      '/api/tenants/resync/' + tenantId,
      'GET',
      {},
    ).then((response) => response.json())
      .then(data => {
        if (data && data.message) {
          let error_msg = data.message;
          if (data.detail) {
            error_msg += "\n"
            for (let i = 0; i < data.detail.length; i++) {
              error_msg += "\n" + data.detail[i].msg;
            }
          }
          if (error_msg) {
            // I can feel the nerd rage eminating from the person reviewing this...
            alert(error_msg);
          }
        } else if (data && data.tenantManagerTenantId) {
          // If successful, we'll get the tenant data back in the JSON response.
          if (data.tenantManagerTenantId === tenantId) {
            alert("Tenant updated on IP successfully!");
          }
        }
      });
  }

  renderTenantReportedClientAdminTable() {
    if (this.state.rows.length === 0) {
      return null;
    }
    return (
      <div>
        Tenant Reported Client Admins
        <table>
          <thead>
            <tr>
              <th>{gettext('First name')}</th>
              <th>{gettext('Last name')}</th>
              <th>{gettext('Email')}</th>
            </tr>
          </thead>
          <tbody>
            { this.state.rows }
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const {
      loadRequestState,
      updateRequestState,
      tenant,
      loggedOnUser,
      setUserFieldCount: setUserFieldCountBound,
      userCount,
    } = this.props;

    const tenantUsers = tenant.get('users', List());
    const isLocking = !tenantUsers.every(Map.isMap);

    const notOfficeAdmin = loggedOnUser.get('is_superuser') || loggedOnUser.get('role').get('id') !== 'OFFICE_ADMIN';

    if (isPending(loadRequestState) || isLocking) {
      return <Loading />;
    }

    return (
      <div>
        <h2>{gettext('Edit client')}</h2>
        <div>{interpolate(gettext('Edit settings for %s'), [tenant.get('name')])}</div>
        <div className='text-right'>
          {
            notOfficeAdmin &&
            <button
              type='button'
              className='button secondary'
              style={{'margin-bottom': '0px'}}
              onClick={() => this.syncToIP(tenant.get('id', ''))}
            >
              {gettext('Sync tenant data to IP')}
            </button>
          }
        </div>

        <EditTenantForm
          onSubmit={(data) => {
            // Newly created user rows may not have sendemail value due to bug
            // in react redux forms not setting default value if checked by default.
            if (Object.prototype.hasOwnProperty.call(data, 'users')) {
              if (data.users.length > 0) {
                data.users = data.users.map((tmpuser) => {
                  if (!Object.prototype.hasOwnProperty.call(tmpuser, 'sendemail')) {
                    tmpuser.sendemail = true;
                  }
                  return tmpuser;
                });
              }
            }
            return this.updateTenantAndNavigateToList(data);
          }}
          onCancel={() => this.navigateToList()}
          requestState={updateRequestState}
          initialValues={tenant
            .filter((value, key) => TENANT_CONFIG_FIELDS.includes(key))
            .set('users', tenantUsers)
            .toJS()
          }
          disabled={
            !canEdit(tenant, loggedOnUser) || !can(loggedOnUser, ACTIONS.UPDATE, MODELS.TENANTS)
          }
          isGa={loggedOnUser.get('is_superuser') || loggedOnUser.get('role').get('id') === 'GLOBAL_ADMIN'}
          anyUsersFailedCreation={tenantUsers.some(isFailedCreation)}
          hideUser={userId => this.confirmAndHideUser(userId)}
          setUserFieldCount={setUserFieldCountBound}
          userCount={userCount}
        />
        { this.renderTenantReportedClientAdminTable() }
      </div>
    );
  }
}

EditTenantPage.propTypes = {
  tenant: ImmutablePropTypes.map.isRequired,
  loggedOnUser: ImmutablePropTypes.map.isRequired,
  loadRequestState: ImmutablePropTypes.map.isRequired,
  updateRequestState: ImmutablePropTypes.map.isRequired,
  loadTenant: PropTypes.func.isRequired,
  updateTenant: PropTypes.func.isRequired,
  resetTenantRequestState: PropTypes.func.isRequired,
  history: ReactRouterPropTypes.history.isRequired,
  lockTenant: PropTypes.func.isRequired,
  unlockTenant: PropTypes.func.isRequired,
  hideTenantUser: PropTypes.func.isRequired,
  openConfirmationModalThen: PropTypes.func.isRequired,
  setUserFieldCount: PropTypes.func.isRequired,
  userCount: PropTypes.number.isRequired,
};

TenantReportedClientAdminRow.propTypes = {
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
};

export function mapStateToProps(state) {
  return {
    tenant: state.items.get(TENANT) || Map(),
    loggedOnUser: state.loggedOnUser,
    loadRequestState: state.requestStates.getIn([LOAD_ITEM, TENANT], Map()),
    updateRequestState: state.requestStates.getIn([UPDATE_ITEM, TENANT], Map()),
    userCount: state.ui.getIn(['userFields', 'userCount'], 0),
  };
}
export function mapDispatchToProps(dispatch, props) {
  const { match: { params: { id } } } = props;
  return {
    loadTenant: () => dispatch(loadItem(TENANT, id)),
    updateTenant: data => dispatch(updateItem(TENANT, id, data)),
    resetTenantRequestState: () => dispatch(resetRequestState(UPDATE_ITEM, TENANT)),
    lockTenant: () => dispatch(lockItem(TENANT, id)),
    unlockTenant: () => dispatch(unlockItem(TENANT, id)),
    hideTenantUser: (tenantId, userId) => dispatch(hideTenantUser(tenantId, userId)),
    openConfirmationModalThen: (message, onConfirm) =>
      dispatch(openConfirmationModalThen(message, onConfirm)),
    setUserFieldCount: count => dispatch(setUserFieldCount(count)),
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(EditTenantPage));
